<template>
    <v-navigation-drawer
        v-model="isDrawerActive"
        app
        fixed
        :rail="lgAndUp"
        :expand-on-hover="lgAndUp"
        :temporary="!lgAndUp"
        :scrim="!lgAndUp"
        :close-on-back="true"
        location="left"
        @click:outside="handleOutsideClick"
        @mouseenter="hovered = true"
        @mouseleave="hovered = false"
    >
        <v-list v-model:opened="openedGroups" nav density="compact">
            <MenuNode v-for="n in menuTree" :key="n.id" :node="n" :show-children="showChildren" />
        </v-list>
        <template #append>
            <v-divider />
            <v-list nav density="compact">
                <v-list-item :title="t('common.releaseNotes.menuTitle')" @click="openReleaseNotes">
                    <template #prepend>
                        <v-icon icon="mdi-newspaper-variant-outline" />
                    </template>
                </v-list-item>
            </v-list>
        </template>
    </v-navigation-drawer>
</template>

<script setup>
/*
    Organization / ordering applied:
    1. Imports
    2. Props / Emits
    3. Constants
    4. Stores & top-level services
    5. Reactive state (refs/reactive)
    6. Validation rules & setup
    7. Computed
    8. Handlers / methods
    9. Helpers / lifecycle
*/

// 1. Imports
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'

import MenuNode from '@/components/MenuNode.vue'
import { errorHandler } from '@/composables/errorHandler'
import { usePermissionHandler } from '@/composables/usePermissionHandler'
import { useNotesStore, useReleaseNotesStore } from '@/stores'

// 2. Props / Emits
const props = defineProps({ drawer: Boolean })
const emit = defineEmits(['update:drawer', 'on-close'])

// 3. Constants
// (none)

// 4. Stores & top-level services
const { lgAndUp } = useDisplay()
const { hasPermission } = usePermissionHandler()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const notesStore = useNotesStore()
const releaseNotesStore = useReleaseNotesStore()
const { unreadProductionSiteNotes } = storeToRefs(notesStore)

// 5. Reactive state (refs/reactive)
const openedGroups = ref([]) // v-list opened model (array of group values)
const hovered = ref(false)
const menuTree = ref([])

// 6. Validation rules & setup
// (none)

// 7. Computed
const isDrawerActive = computed({
    get() {
        return props.drawer
    },
    set(v) {
        emit('update:drawer', v)
    }
})

// Show children only when not rail, or when rail is expanded on hover
const showChildren = computed(() => !lgAndUp.value || hovered.value)

// 8. Handlers / methods
const handleOutsideClick = () => {
    emit('update:drawer', false)
}

const openReleaseNotes = async () => {
    try {
        await releaseNotesStore.fetchLatestVersion()
        releaseNotesStore.setShowDialog(true)
    } catch (error) {
        errorHandler(error)
    }
}

const toNode = (route, parentPath = '') => {
    if (!route.meta || !route.meta.menuRendered) return null
    if (route.meta.permission && !hasPermission(route.meta.permission)) return null
    if (!route.name) return null

    const menuTitleKey = route.meta.menuTitle || route.meta.title || route.name || route.path
    let badge = route.meta.badge

    // Build absolute path
    const absolutePath = route.path.startsWith('/') ? route.path : `${parentPath}/${route.path}`.replace(/\/+/g, '/')

    // Dynamic badge for notes route
    if (route.name === 'notes' || route.path === '/notes') {
        const unreadCount = unreadProductionSiteNotes.value
        if (unreadCount > 0) {
            badge = unreadCount
        }
    }

    const id = route.name
    const to = { name: route.name }

    const node = {
        id,
        menuTitle: t(menuTitleKey),
        name: route.name,
        to,
        icon: route.meta.icon,
        badge,
        children: []
    }

    const children = route.children || []

    children.forEach((c) => {
        const child = toNode(c, absolutePath)

        if (child) node.children.push(child)
    })

    if (children.length) {
        const canShowSelf = !!route.meta.permission
        const hasVisibleChildren = node.children.length > 0

        if (!canShowSelf && !hasVisibleChildren) return null
    }

    if (!node.children.length) delete node.children

    return node
}

const buildMenu = () => {
    const routes = router?.options?.routes || []
    menuTree.value = routes.map(toNode).filter(Boolean)
}

const collectGroupIds = (nodes, set) => {
    nodes.forEach((n) => {
        if (n.children && n.children.length) {
            set.add(n.id)
            collectGroupIds(n.children, set)
        }
    })
}

const syncOpenedWithRoute = () => {
    if (!menuTree.value.length) return
    const groupIds = new Set()
    collectGroupIds(menuTree.value, groupIds)

    const matchedIds = (route.matched || []).map((m) => m.name || m.path).filter(Boolean)
    const chain = matchedIds.filter((id) => groupIds.has(id))

    openedGroups.value = showChildren.value ? chain : []
}

// Watch for unread notes count changes to update menu badge
watch(
    () => unreadProductionSiteNotes.value,
    (newValue, oldValue) => {
        // Only rebuild menu if value actually changed
        if (newValue !== oldValue) {
            buildMenu()
            syncOpenedWithRoute()
        }
    },
    { immediate: true }
)

// 9. Helpers / lifecycle
onMounted(() => {
    buildMenu()
    syncOpenedWithRoute()
})

watch(
    () => route.path,
    () => {
        syncOpenedWithRoute()
    }
)

watch(showChildren, () => {
    syncOpenedWithRoute()
})
</script>
